import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { useFormik } from "formik";

import {
  RequestRecoverPassword,
  useRecoverPasswordMutation,
  useToast,
} from "@/hooks";
import { Button, Input, Label } from "@/components/ui";
import { Link } from "react-router-dom";

const initialValues = {
  email: "",
};

export function ForgotPassword() {
  const { t } = useTranslation();
  const { toast } = useToast();

  const { mutateAsync, isLoading } = useRecoverPasswordMutation();

  const forgotPasswordSchema = Yup.object().shape({
    email: Yup.string()
      .email(t("LABEL.invalid email"))
      .min(3, t("LABEL.min"))
      .max(50, t("LABEL.max"))
      .required(t("LABEL.required")),
  });

  const { handleSubmit, ...formik } = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: async (values) => {
      handleRecoverPasswordSubmit(values);
    },
  });

  async function handleRecoverPasswordSubmit(request: RequestRecoverPassword) {
    try {
      await mutateAsync(request, {
        onSuccess: () => {
          formik.resetForm();
          toast({
            title: t("LABEL.success"),
            description: t(
              "MESSAGE.your email has been sent successfully. Please check your email inbox"
            ),
            variant: "default",
          });
        },
        onError: () => {
          toast({
            title: t("LABEL.error"),
            description: t("MESSAGE.error_message"),
            variant: "destructive",
          });
        },
      });

    } catch {
      toast({
        title: t("LABEL.error"),
        description: t(`MESSAGE.error`),
        variant: "destructive",
      });
    }
  }

  return (
    <div className="max-w-[373px] w-full min-h-[596px] bg-white rounded-xl p-10 space-y-11 flex flex-col">
      <div className="flex flex-col justify-center items-center gap-4">
        <img
          width={140}
          height={70}
          src="/media/logos/logo-medium.svg"
          alt="Clinisharp logo"
          className="object-contain"
        />
        <h1 className="text-3xl font-bold text-zinc-700">
          {t("recover password")}
        </h1>
        <p className="text-sm text-app-fg-muted text-center">
          {t("clinic.forgot_password.enter your email to reset your password")}
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex-1 flex flex-col justify-between h-full"
      >
        <div className="relative flex flex-col gap-2 w-full">
          <Label className="text-sm text-zinc-500 font-normal">
            {t("form.email")}
          </Label>
          <Input
            type="email"
            placeholder={t("form.email")}
            {...formik.getFieldProps("email")}
            isInvalid={formik.touched.email && formik.errors.email}
            isValid={formik.touched.email && !formik.errors.email}
            messageError={formik.errors.email}
          />
        </div>

        <div className="flex flex-col gap-5">
          <Button
            size="lg"
            type="submit"
            className="w-full bg-teal-700 hover:bg-teal-800"
            disabled={isLoading}
            isLoading={isLoading}
          >
            {t("actions.send")}
          </Button>
          <Link
            to="/auth"
            className="text-center text-sm underline flex justify-center items-center gap-3 w-full text-teal-600 bg-transparent outline-none border-none"
          >
            {t("LABEL.login")}
          </Link>
        </div>
      </form>
    </div>
  );
}
